import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, filter, map, shareReplay } from 'rxjs/operators';
import { AppState } from 'src/app/root-store/app-state';
import { getShareIdSelector } from 'src/app/root-store/root-store.selector';
import { AppConfigService } from '../services/app.config.service';
import { CurrentUser } from './currentUser';
import { UmsgqlProfileService } from './umsgql-profile.service';

@Injectable({
  providedIn: 'root',
})
export class CurrentUserService {
  currentUser$: Observable<CurrentUser>;

  constructor(
    private httpClient: HttpClient,
    private appConfigService: AppConfigService,
    private store$: Store<AppState>,
    private umsgqlProfileService: UmsgqlProfileService,
  ) {
    this.store$
      .select(getShareIdSelector)
      .pipe(
        filter((x) => !!x && x.hasState),
        map((x) => x.id),
      )
      .subscribe((id) => {
        if (!id) {
          this.currentUser$ = this.getCurrentUser().pipe(shareReplay());
        }
      });
  }

  private getCurrentUser(): Observable<CurrentUser> {
    const { baseUrl, currentUser } = this.appConfigService.settings.api.endpoints;
    return this.httpClient.get<CurrentUser>(baseUrl + currentUser);
  }

  getCurrentUserProfile(): Observable<CurrentUser> {
    return forkJoin({
      orms: this.getCurrentUser(),
      umsgql: this.umsgqlProfileService.getUserProfile().pipe(catchError(() => of(null))),
    }).pipe(
      map(({ orms, umsgql }) => ({
        ...orms,
        firstName: umsgql?.givenName || orms.firstName,
        lastName: umsgql?.surname || orms.lastName,
        // Only fall back to ORMS mobilePhone when the UMSGQL call itself failed (umsgql === null).
        // When UMSGQL returns null explicitly the user cleared their number — respect that.
        mobilePhone: umsgql !== null ? umsgql.mobilePhone : orms.mobilePhone ?? null,
      })),
    );
  }

  updateCurrentUserProfile(profile: {
    email: string;
    firstName: string;
    lastName: string;
    mobilePhone?: string | null;
  }): Observable<CurrentUser> {
    return this.umsgqlProfileService
      .updateUserProfile(profile.email, profile.firstName, profile.lastName, profile.mobilePhone ?? null)
      .pipe(
        map(
          (umsgqlProfile) =>
            ({
              email: umsgqlProfile.email ?? profile.email,
              firstName: umsgqlProfile.givenName ?? profile.firstName,
              lastName: umsgqlProfile.surname ?? profile.lastName,
              mobilePhone: umsgqlProfile.mobilePhone,
            }) as CurrentUser,
        ),
      );
  }
}
